import {Helmet} from '@dr.pogodin/react-helmet';
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';

import {isEmpty, isNil} from 'ramda';

import {useGetTransferOrderVariantsQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {logError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {
  TransferOrderCreateForm,
  TransferOrderCreateFormSchema,
} from './types/TransferOrderCreateForm';

export function TransferOrderCreate() {
  const navigate = useNavigate();

  const {options, isLoadingOptions} = useGetTransferOrderVariantsQuery(
    {},
    {
      selectFromResult: (result) => ({
        options: result.data?.map((transferOrderVariant) => ({
          label: transferOrderVariant?.name,
          value: transferOrderVariant?.transferOrderVariantId,
        })),
        isLoadingOptions: result.isLoading,
      }),
    }
  );

  const handleSubmit: FormSubmitHandler<TransferOrderCreateForm> = async (formValues) => {
    if (isNil(formValues.transferOrderVariantId) || isEmpty(formValues.transferOrderVariantId)) {
      return logError('transferOrderVariant is not defined');
    }

    await navigate(
      composePath(warehouseRoutes.transferOrderDraft, {
        params: {transferOrderVariantId: formValues.transferOrderVariantId},
      })
    );
  };

  const handleDiscard = () => {
    navigate(warehouseRoutes.transfers);
  };

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.labels.newTransferOrder')} />
      <Dialog
        isOpen
        title={i18n.t('page.warehouse.labels.newTransferOrder')}
        size="small"
        withAdditionalFooter
        scrollBehavior="outside"
        onClose={handleDiscard}
      >
        <Form<TransferOrderCreateForm>
          experimentalZodSchema={TransferOrderCreateFormSchema}
          defaultValues={{transferOrderVariantId: ''}}
          onSubmit={handleSubmit}
        >
          {(control) => (
            <>
              <FormField
                isRequired
                isNotClearable
                isLoading={isLoadingOptions}
                control={control}
                name="transferOrderVariantId"
                type="choice"
                label={i18n.t('page.warehouse.labels.transferOrderVariant')}
                options={options ?? []}
              />
              <DialogFooter>
                <ButtonGroup align="right">
                  <Button
                    type="button"
                    title={i18n.t('general.actions.discard')}
                    variant="secondary"
                    onClick={handleDiscard}
                  />
                  <FormButton
                    control={control}
                    type="submit"
                    title={i18n.t('general.actions.continue')}
                  />
                </ButtonGroup>
              </DialogFooter>
            </>
          )}
        </Form>
      </Dialog>
    </>
  );
}
